import { openDB } from 'idb';
import { addEventLog } from 'utils/functions/eventLogFunctions';
import { DataPointSyncVO, SampleLocationAllVO, GroupAllVO ,SiteAllVO, DeletionSyncCache} from 'utils/models/sync-catch';

const dbName = 'testResultsDB';
const storeName = 'resultsStore';

export const initDB = async () => {
	const db = await openDB(dbName, 1, {
		upgrade(db) {
			if (!db.objectStoreNames.contains(storeName)) {
				db.createObjectStore(storeName);
			}
		},
	});
	return db;
};

export const saveTestResults = async (
	key: string,
	data: any[],
	username: string
) => {
	const db = await initDB();
	const userSpecificKey = `${username}_${key}`;
	const timestampedData = Array.isArray(data)
		? data.map((item) => ({ ...item, timestamp: item?.timestamp ?  item?.timestamp : new Date().getTime() }))
		: [];
	await db.put(storeName, timestampedData, userSpecificKey);
};

export const cleanupTestResults = async () => {
	const db = await initDB();
	const tx = db.transaction(storeName, 'readwrite');
	const store = tx.objectStore(storeName);
	const allKeys = await store.getAllKeys();

	for (const key of allKeys) {
		if (typeof key === 'string' && key.includes('_')) {
			const data = await store.get(key);
			if (data && new Date().getTime() - data.timestamp > 1800000) {
				await store.delete(key);
			}
		}
	}

	await tx.done;
};


export	const saveSyncCache = async (
	cache:
		GroupAllVO | SiteAllVO | SampleLocationAllVO | DataPointSyncVO,
	name:
		| "groupSyncCache"
		| "siteSyncCache"
		| "locationSyncCache"
		| "dataPointSyncCache"
	) => {
	const db = await initDB();
	const existingCache = (await db.get(storeName, name)) || [];

	let newCache;
	let existingEntryIndex = existingCache.findIndex((entry: {id: number}) => entry.id === cache.id);

	if (existingEntryIndex > -1) {
		cache.action = existingCache[existingEntryIndex].action;
		existingCache[existingEntryIndex] = cache;
		newCache = [...existingCache];
	} else {
		newCache = [...existingCache, cache];
	}
	try{
	await db.put(storeName, newCache, name);
	addEventLog("Sync Cache Saved Success", "Sync", undefined, undefined, `Cache Type: ${name}, Cache Data: ${JSON.stringify(cache)}`);
	}catch(error){
		addEventLog("Sync Cache Save Failed", "Sync", undefined, undefined, `Cache Type: ${name}, Cache Data: ${JSON.stringify(cache)}, Error: ${error}`);
	}
};

export const saveDeletionSyncCache = async (
  cache: DeletionSyncCache,
  name: "deletionSyncCache"
) => {
  const db = await initDB();
  const existingCache = (await db.get(storeName, name)) || {
    deletedGroups: [],
    deletedSites: [],
    deletedLocations: [], 
    deletedDataPoints: []
  };

  //If we are deleting something that has not been synced yet, we only need to remove it from the sync cache here.
  let deletedId = 0;
  let existingCacheName = "groupSyncCache";
  let locationFlag = false;
  if (cache?.deletedGroups?.[0]) {

	deletedId = cache.deletedGroups[0];

  } else if (cache?.deletedSites?.[0]) {

	existingCacheName = "siteSyncCache";
	deletedId = cache.deletedSites[0];

  } else if (cache?.deletedLocations?.[0]) {

	existingCacheName = "locationSyncCache";
	deletedId = cache.deletedLocations[0];
	locationFlag = true;

  } else if (cache?.deletedDataPoints?.[0]) {

	existingCacheName = "dataPointSyncCache";
	deletedId = cache.deletedDataPoints[0];

  }

  let pendingSyncEntries = await db.get(storeName, existingCacheName);
  let deletedEntryIndex = pendingSyncEntries.findIndex((entry: {id: number}) => entry.id === deletedId);
  try{
  if (deletedEntryIndex > -1) {

	pendingSyncEntries.splice(deletedEntryIndex, 1);
	await db.put(storeName, [...pendingSyncEntries], existingCacheName);
	addEventLog("Deletion Sync Cache Updated Success", "Sync", undefined, undefined, `Cache Type: ${existingCacheName}, Deleted ID: ${deletedId}`);
	if (locationFlag) {

		//We need to take care of pending data points inside a location that is now removed from the sync cache
		let pendingSyncDataPoints = await db.get(storeName, "dataPointSyncCache");
		let indexesToDelete = [];
		let idsToDelete = [];
		for (let i = 0; i < pendingSyncDataPoints.length; i++) {
			if (pendingSyncDataPoints[i].locationFK === deletedId) {
				indexesToDelete.push(i);
				idsToDelete.push(pendingSyncDataPoints[i].id);
			}
		}
		indexesToDelete.forEach(index => {
			pendingSyncDataPoints.splice(index, 1);
		});

		if (indexesToDelete.length > 0) {
			await db.put(storeName, [...pendingSyncDataPoints], "dataPointSyncCache");
			addEventLog("Deletion Sync Cache Updated Success", "Sync", undefined, undefined, `Cache Type: dataPointSyncCache, Deleted Data Point IDs: ${idsToDelete.join(", ")} `);
		}

	}
	
  } else {

	const newCache = {
		deletedGroups: [
		...(existingCache?.deletedGroups || []),
		...(cache?.deletedGroups || [])
		],
		deletedSites: [
		...(existingCache?.deletedSites || []),
		...(cache?.deletedSites || [])
		],
		deletedLocations: [
		...(existingCache?.deletedLocations || []),
		...(cache?.deletedLocations || [])
		],
		deletedDataPoints: [
		...(existingCache?.deletedDataPoints || []),
		...(cache?.deletedDataPoints || [])
		]
	};

	await db.put(storeName, newCache, name);

  }
}catch(error){
	addEventLog("Deletion Sync Cache Update Failed", "Sync", undefined, undefined, `Cache Type: ${name}, Cache Data: ${JSON.stringify(cache)}, Error: ${error}`);
}
};

export const emptyAllCatchSync = async () => {
	const db = await initDB();
	try{
	await db.put(storeName, [], "groupSyncCache");
	await db.put(storeName, [], "siteSyncCache");
	await db.put(storeName, [], "locationSyncCache");
	await db.put(storeName, [], "dataPointSyncCache");
	await db.put(storeName, { deletedGroups: [], deletedSites: [], deletedLocations: [], deletedDataPoints: [] }, "deletionSyncCache");
	addEventLog("Empty Sync Cache Success", "Sync");
	}
	catch(error){
		addEventLog("Empty Sync Cache Failed", "Sync", undefined, undefined, `Error: ${error}`);
	}
}

export const getSyncCache = async () => {
	const db = await initDB();
	const groupSyncs = await db.get(storeName, "groupSyncCache");
	const siteSyncs = await db.get(storeName, "siteSyncCache");
	const locationSyncs = await db.get(storeName, "locationSyncCache");
	const dataPointSyncs = await db.get(storeName, "dataPointSyncCache");
	const deletionSync = await db.get(storeName, "deletionSyncCache");
	return { groupSyncs, siteSyncs, locationSyncs, dataPointSyncs, deletionSync };
};


//Gets any key. Not just testing results.
export const getTestResults = async (key: string, username: string) => {
	const db = await initDB();
	const userSpecificKey = `${username}_${key}`;
	return await db.get(storeName, userSpecificKey);
};

export const saveAuthToken = async (token: string) => {
	const db = await initDB();
	await db.put(storeName, token, 'authToken');
};

export const getAuthToken = async () => {
	const db = await initDB();
	return await db.get(storeName, 'authToken');
};

export const saveFailedRequest = async (data: any) => {
	const db = await initDB();
	const uniqueKey = `failed_${new Date().getTime()}`;
	try{
		await db.put(storeName, data, uniqueKey);
		addEventLog("Failed Request - Save Success", "Sync");
	}catch(error){
		addEventLog("Failed Request - Save Failed", "Sync", undefined, undefined, `Error: ${error}`);
	}
};

export const getAllFailedRequests = async () => {
	const db = await initDB();
	const tx = db.transaction(storeName, 'readonly');
	const store = tx.objectStore(storeName);
	const allKeys = await store.getAllKeys();

	const failedRequestsKeys = allKeys.filter(
		(key) => typeof key === 'string' && key.startsWith('failed_')
	);

	const failedRequests = await Promise.all(
		failedRequestsKeys.map((key) => store.get(key))
	);

	//Gather bodies into an array, and if one body is already in the array, discard that request.
	//Attempts to address duplication of 'createdatapointresult'
	let reqBodies: string[] = [];
	const uniqueRequests = failedRequests.filter((req: any, index) => {
		if (reqBodies.includes(req.body)) {
			removeFailedRequest(JSON.stringify(failedRequestsKeys[index]));
			return false;
		} else {
			reqBodies.push(req.body);
			return true;
		}
	});

	return uniqueRequests.map((request, index) => ({
		...request,
		key: failedRequestsKeys[index],
	}));
};

export const updateFailedRequest = async (data: any, key: string) => {
	const db = await initDB();
	try{
		await db.put(storeName, data, key);
		addEventLog("Failed Request Updated Success", "Sync", undefined, undefined, `Key: ${key}`);
	}catch(error){
		addEventLog("Failed Request Update Failed", "Sync", undefined, undefined, `Key: ${key}, Error: ${error}`);
	}
}

export const removeFailedRequest = async (key: string) => {
	const db = await initDB();
	try{
		await db.delete(storeName, key);
		addEventLog("Failed Request Removed Success", "Sync", undefined, undefined, `Key: ${key}`);
	}catch(error){
		addEventLog("Failed Request Removal Failed", "Sync", undefined, undefined, `Key: ${key}, Error: ${error}`);
	}
};

export const saveSwitchStateToDB = async (
	key: string,
	switchState: boolean,
	username: string
) => {
	const db = await initDB();
	const userSpecificKey = `${username}_${key}`;
	try{
		await db.put(storeName, switchState, userSpecificKey);
		addEventLog("Switch State Saved Success", "Sync", undefined, undefined, `Key: ${key}, Username: ${username}`);
	}catch(error){
		addEventLog("Switch State Save Failed", "Sync", undefined, undefined, `Key: ${key}, Username: ${username}, Error: ${error}`);
	}
};

export const getSwitchStateFromDB = async (key: string, username: string) => {
	const db = await initDB();
	const userSpecificKey = `${username}_${key}`;
	return await db.get(storeName, userSpecificKey);
};

export const saveCalibrationResult = async (
	inputId: string,
	calibrationData: any,
	username: string
) => {
	const db = await initDB();
	const timestamp = new Date().getTime();
	const userSpecificKey = `${username}_${inputId}_calibration`;
	await db.put(storeName, { calibrationData, timestamp }, userSpecificKey);
};

export const getCalibrationResult = async (
	inputId: string,
	username: string
) => {
	const db = await initDB();
	const userSpecificKey = `${username}_${inputId}_calibration`;
	const result = await db.get(storeName, userSpecificKey);
	if (
		result &&
		new Date().getTime() - result.timestamp <= 2 * 60 * 60 * 1000
	) {
		return result;
	}
	return null;
};

export const cleanupCalibrationData = async () => {
	const db = await initDB();
	const tx = db.transaction(storeName, 'readwrite');
	const store = tx.objectStore(storeName);
	const allKeys = await store.getAllKeys();

	for (const key of allKeys) {
		// Check if the key is a string and contains '_calibration'
		if (typeof key === 'string' && key.includes('_calibration')) {
			const data = await store.get(key);
			if (
				data &&
				new Date().getTime() - data.timestamp > 2 * 60 * 60 * 1000
			) {
				// 2 hours
				await store.delete(key);
			}
		}
	}

	await tx.done;
};

export const deleteUserData = async (email: string, siteID?: number) => {
	const db = await initDB();
	const tx = db.transaction(storeName, 'readwrite');
	const store = tx.objectStore(storeName);
	const allKeys = await store.getAllKeys();
	for (const key of allKeys) {
		if (typeof key === 'string' && key.startsWith(email)) {
			await store.delete(key);
		}
	}

	await tx.done;
};